import { useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "./ui/dialog";
import { MessageSquarePlus, Loader2, GraduationCap, Wrench } from "lucide-react";
import { toast } from "sonner";

const TYPES = {
  curriculum: ["Something was confusing", "A lesson could be improved", "The pacing felt off", "Teaching suggestion", "General feedback"],
  platform: ["I found a bug", "Something didn't load", "Progress wasn't saved", "Quiz issue", "General feedback"],
};

export const BetaFeedbackButton = ({ className = "" }) => {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("curriculum");
  const [type, setType] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => { setCategory("curriculum"); setType(""); setMessage(""); };

  const submit = async () => {
    if (!message.trim()) { toast.error("Please add a short description."); return; }
    setSubmitting(true);
    try {
      await api.post("/feedback", { category, type: type || null, message: message.trim(), source: "nav" });
      toast.success("Thank you — your feedback helps us improve DronAcademy.");
      setOpen(false); reset();
    } catch (e) {
      toast.error("Could not send feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <button data-testid="nav-beta-feedback"
          className={`flex items-center gap-2 px-3.5 py-2 rounded-sm text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors ${className}`}>
          <MessageSquarePlus className="h-4 w-4" /> Beta Feedback
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="beta-feedback-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Beta Feedback</DialogTitle>
          <DialogDescription>Tell us what to fix or improve. We read every note.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">What's this about?</span>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {[["curriculum", "Curriculum", GraduationCap], ["platform", "Platform", Wrench]].map(([val, label, Icon]) => (
                <button key={val} type="button" onClick={() => { setCategory(val); setType(""); }}
                  data-testid={`feedback-category-${val}`}
                  className={`flex items-center justify-center gap-2 h-10 rounded-sm border text-sm font-medium transition-colors ${
                    category === val ? "border-primary/60 bg-primary/10 text-primary" : "border-border/70 bg-secondary/30 text-muted-foreground hover:text-foreground"}`}>
                  <Icon className="h-4 w-4" /> {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Type</span>
            <div className="flex flex-wrap gap-2 mt-2">
              {TYPES[category].map((t) => (
                <button key={t} type="button" onClick={() => setType(t)} data-testid={`feedback-type-${t}`}
                  className={`px-3 py-1.5 rounded-sm border text-xs transition-colors ${
                    type === t ? "border-primary/60 bg-primary/10 text-primary" : "border-border/70 bg-secondary/30 text-muted-foreground hover:text-foreground"}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div>
            <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Details</span>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} data-testid="feedback-message"
              placeholder="Describe what you noticed..." className="mt-2 bg-secondary/40 border-border min-h-[110px]" />
          </div>

          <Button onClick={submit} disabled={submitting} data-testid="feedback-submit"
            className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-semibold">
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send feedback
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
